/**
 * Translation prefetch
 *
 * After a language switch we proactively warm the client cache for the strings
 * a visitor is most likely to hit next: the main navigation labels and the
 * hero / first-screen copy of every primary route.
 *
 * Strings live as a flat list here so we can extend without scraping pages.
 */
import { recordChunk, recordCache } from "@/lib/translateTelemetry";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate`;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const SOURCE_LANG = "de";

// Navigation + first-screen copy that appears on most pages.
export const PREFETCH_STRINGS: string[] = [
  // Navigation
  "Startseite",
  "Ferienwohnungen",
  "Haus Panorama Duhnen",
  "Haus Strandnixe Döse",
  "Wissenswertes",
  "Die Umgebung",
  "Bildergalerie",
  "Kontakt",
  "Impressum",
  "Datenschutzerklärung",
  // Common CTAs
  "Jetzt anrufen",
  "Verfügbarkeit prüfen",
  "Mehr erfahren",
  "Zur Wohnung",
  "Alle Wohnungen ansehen",
  "Kontakt aufnehmen",
  // Hero & first-screen
  "Ferienwohnungen in Cuxhaven-Duhnen an der Nordsee",
  "Strandnahe Ferienwohnungen in Cuxhaven mieten – persönlich betreut seit 1986. Ideal für Paare, Familien und erholsamen Nordseeurlaub.",
  "Cuxhaven-Duhnen • Nordsee",
  "Ihre Gastgeber seit 1986",
  "Familie Luca",
  "Inhaber & persönliche Ansprechpartner vor Ort",
  "Strand & Meer in Cuxhaven",
  "Urlaub direkt an der Nordsee",
  "Überzeugt? Buchen Sie Ihren Nordsee-Urlaub!",
  // Footer
  "Folgen Sie uns",
  "Rechtliches",
  "Schnellzugriff",
];

const cacheKey = (lang: string, src: string) => `tr::${lang}::${src}`;

const isCached = (lang: string, src: string): boolean => {
  try {
    return localStorage.getItem(cacheKey(lang, src)) !== null;
  } catch {
    return false;
  }
};

const setCached = (lang: string, src: string, translated: string) => {
  try {
    localStorage.setItem(cacheKey(lang, src), translated);
  } catch {}
};

let inFlight: Record<string, Promise<void>> = {};

export const prefetchTranslations = async (lang: string): Promise<void> => {
  if (lang === SOURCE_LANG) return;
  if (inFlight[lang]) return inFlight[lang];

  const work = (async () => {
    const missing = PREFETCH_STRINGS.filter((s) => !isCached(lang, s));
    const hits = PREFETCH_STRINGS.length - missing.length;
    recordCache(lang, hits, missing.length);
    if (missing.length === 0) return;

    // Single batch — small and bounded.
    const start = performance.now();
    try {
      const resp = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({ texts: missing, target: lang, source: SOURCE_LANG }),
      });
      const latency = performance.now() - start;
      if (!resp.ok) {
        recordChunk(lang, missing.length, latency, false);
        return;
      }
      const data = await resp.json();
      const translations = (data.translations as string[]) || [];
      missing.forEach((src, idx) => {
        const tr = translations[idx];
        if (tr) setCached(lang, src, tr);
      });
      recordChunk(lang, missing.length, latency, true);
    } catch (e) {
      recordChunk(lang, missing.length, performance.now() - start, false);
      console.error("Prefetch failed", e);
    }
  })();

  inFlight[lang] = work;
  try {
    await work;
  } finally {
    delete inFlight[lang];
  }
};
